import React, { useContext, useEffect, useState } from "react";
import { Button, Col, Container, Form, Modal, Row } from "react-bootstrap";
import ErrorDisplay from "../common/ErrorDisplay";
import { Problem } from "../api/backendApi";
import { drawingApi, StyleModel } from "../api/drawingApi";
import { DrawingContext } from "./DrawingContext";

interface StyleEditModalProps {
  name: string | undefined;
  readOnly: boolean;
  onClose: () => void;
  onSave(name: string): void;
}

function StyleEditModal({
  name,
  readOnly: initialReadOnly,
  onClose,
  onSave,
}: StyleEditModalProps) {
  const drawing = useContext(DrawingContext);
  const [error, setError] = useState<string | Problem | undefined>();
  const [style, setStyle] = useState<StyleModel>({});
  const [readOnly, setReadOnly] = useState<boolean>(true);

  useEffect(() => {
    async function load() {
      if (name) {
        const response = await drawingApi.getStyle(
          drawing.name,
          drawing.type,
          name,
        );

        if (response.ok) {
          setError(undefined);
          setStyle(response.value);
          setReadOnly(initialReadOnly);
        } else {
          setError(response.problem);
        }
      } else {
        setReadOnly(initialReadOnly);
      }
    }

    load();
  }, [initialReadOnly, name, drawing.name, drawing.type]);

  const handleClose = () => {
    setError(undefined);
    onClose();
  };

  const handleSave = async () => {
    if (!style.name || style.name.length === 0) {
      setError("A name is required.");
      return;
    }

    const result = await drawingApi.upsertStyle(
      drawing.name,
      drawing.type,
      name,
      style,
    );

    if (result.ok) {
      setError(undefined);

      onSave(style.name);
      onClose();
    } else {
      setError(result.problem);
    }
  };

  const convertNumber = (value: string) => {
    const number = parseFloat(value);

    return Number.isFinite(number) ? number : undefined;
  };

  return (
    <Modal show={true} onHide={() => handleClose()}>
      <Modal.Header>
        <Modal.Title>{name ?? "New Style"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container>
          <ErrorDisplay error={error} />
          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  value={style.name ?? ""}
                  readOnly={readOnly}
                  onChange={(e) => setStyle({ ...style, name: e.target.value })}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Stroke width (px): </Form.Label>
                <Form.Control
                  value={style.strokeWidth ?? ""}
                  readOnly={readOnly}
                  onChange={(e) =>
                    setStyle({
                      ...style,
                      strokeWidth: convertNumber(e.currentTarget.value),
                    })
                  }
                  onKeyDown={(e) => {
                    if (
                      e.key !== "." &&
                      (e.key < "0" || e.key > "9") &&
                      e.key !== "Backspace" &&
                      e.key !== "Tab" &&
                      e.key !== "Delete"
                    )
                      e.preventDefault();
                  }}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Stroke color: </Form.Label>
                <Form.Control
                  value={style.strokeColor ?? ""}
                  readOnly={readOnly}
                  onChange={(e) =>
                    setStyle({
                      ...style,
                      strokeColor: e.currentTarget.value.trim(),
                    })
                  }
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Stroke opacity: </Form.Label>
                <Form.Control
                  value={style.strokeOpacity?.toString() ?? ""}
                  readOnly={readOnly}
                  onChange={(e) =>
                    setStyle({
                      ...style,
                      strokeOpacity: convertNumber(e.currentTarget.value),
                    })
                  }
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Fill color: </Form.Label>
                <Form.Control
                  value={style.fillColor ?? ""}
                  readOnly={readOnly}
                  onChange={(e) =>
                    setStyle({
                      ...style,
                      fillColor: e.currentTarget.value.trim(),
                    })
                  }
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Fill opacity: </Form.Label>
                <Form.Control
                  value={style.fillOpacity?.toString() ?? ""}
                  readOnly={readOnly}
                  onChange={(e) =>
                    setStyle({
                      ...style,
                      fillOpacity: convertNumber(e.currentTarget.value),
                    })
                  }
                />
              </Form.Group>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={() => handleClose()}>Cancel</Button>
        <Button disabled={readOnly} onClick={() => handleSave()}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default StyleEditModal;
